import type { RouteTable, RouteFunction } from '$classes/Router'
import { isLogged, isAdmin, reverseAuth, reverseAdmin } from '$lib/guards'

export type LayoutKind = 'user' | 'backoffice' | 'auth' | 'plain'

export type RoutesContext = {
    setPage: (Component: unknown) => void
    setLayout: (layout: LayoutKind) => void
}

export function buildRoutes(ctx: RoutesContext): RouteTable {
    const userLayout: RouteFunction = (_a, next) => {
        ctx.setLayout('user')
        next()
    }
    const authLayout: RouteFunction = (_a, next) => {
        ctx.setLayout('auth')
        next()
    }
    const backofficeLayout: RouteFunction = (_a, next) => {
        ctx.setLayout('backoffice')
        next()
    }
    const plainLayout: RouteFunction = (_a, next) => {
        ctx.setLayout('plain')
        next()
    }

    return {
        '/': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/index.svelte')).default)
        ],
        '/login': [
            reverseAuth,
            authLayout,
            async () => ctx.setPage((await import('$pages/auth/login.svelte')).default)
        ],
        '/register': [
            reverseAuth,
            authLayout,
            async () => ctx.setPage((await import('$pages/auth/register.svelte')).default)
        ],
        '/register/verify-mail': [
            authLayout,
            async () => ctx.setPage((await import('$pages/auth/register/verify-mail.svelte')).default)
        ],
        '/passwordRecover': [
            authLayout,
            async () => ctx.setPage((await import('$pages/auth/passwordRecover.svelte')).default)
        ],
        '/passwordRecover/step-2': [
            authLayout,
            async () => ctx.setPage((await import('$pages/auth/passwordRecover/step-2.svelte')).default)
        ],
        '/backoffice': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/index.svelte')).default)
        ],
        '/backoffice/login': [
            reverseAdmin,
            authLayout,
            async () => ctx.setPage((await import('$pages/backoffice/login.svelte')).default)
        ],
        '/backoffice/passwordRecover': [
            authLayout,
            async () => ctx.setPage((await import('$pages/backoffice/passwordRecover.svelte')).default)
        ],
        '/backoffice/reset-password': [
            authLayout,
            async () => ctx.setPage((await import('$pages/backoffice/reset-password.svelte')).default)
        ],
        '/backoffice/profile': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/profile.svelte')).default)
        ],
        '/backoffice/profile/change-password': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/profile/change-password.svelte')).default)
        ],
        '/backoffice/remove-contract': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/remove-contract.svelte')).default)
        ],
        '/backoffice/clients': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/clients/index.svelte')).default)
        ],
        '/backoffice/clients/:id/history': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/clients/[id]/history.svelte')).default)
        ],
        '/backoffice/dictionaries': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/dictionaries/index.svelte')).default)
        ],
        '/backoffice/dictionaries/:id/edit': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/dictionaries/[id]/edit.svelte')).default)
        ],
        '/backoffice/partners': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/partners/index.svelte')).default)
        ],
        '/backoffice/partners/add': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/partners/add.svelte')).default)
        ],
        '/backoffice/partners/edit-footer': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/partners/edit-footer.svelte')).default)
        ],
        '/backoffice/partners/:id/edit': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/partners/[id]/edit.svelte')).default)
        ],
        '/backoffice/users': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/users/index.svelte')).default)
        ],
        '/backoffice/users/add': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/users/add.svelte')).default)
        ],
        '/backoffice/users/:id/edit': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/users/[id]/edit.svelte')).default)
        ],
        '/backoffice/videos': [
            isAdmin,
            backofficeLayout,
            async () => ctx.setPage((await import('$pages/backoffice/videos/index.svelte')).default)
        ],
        '/maintenance': [
            plainLayout,
            async () => ctx.setPage((await import('$pages/maintenance.svelte')).default)
        ],
        '/profile': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/profile.svelte')).default)
        ],
        '/profile/change-password': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/profile/change-password.svelte')).default)
        ],
        '/assign-contract/:token': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/assign-contract/[token].svelte')).default)
        ],
        '/policy/new/retail': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/new/retail.svelte')).default)
        ],
        '/policy/:id/detail': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/[id]/detail.svelte')).default)
        ],
        '/policy/:id/faq': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/[id]/faq.svelte')).default)
        ],
        '/policy/:id/claims': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/[id]/claims/index.svelte')).default)
        ],
        '/policy/:id/claims/new': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/[id]/claims/new.svelte')).default)
        ],
        '/policy/:id/claims/:claimId/detail': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/[id]/claims/[claimId]/detail.svelte')).default)
        ],
        '/policy/:id/claims/:claimId/integrate': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/[id]/claims/[claimId]/integrate.svelte')).default)
        ],
        '/policy/:id/claims/:claimId/integrate/new': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/[id]/claims/[claimId]/integrate/new.svelte')).default)
        ],
        '/policy/:id/claims/:claimId/continue-draft': [
            isLogged,
            userLayout,
            async () => ctx.setPage((await import('$pages/policy/[id]/claims/[claimId]/continue-draft.svelte')).default)
        ],
        '/generic-error': [
            plainLayout,
            async () => ctx.setPage((await import('$pages/generic-error.svelte')).default)
        ],
        __default: [
            plainLayout,
            async () => ctx.setPage((await import('$pages/not-found.svelte')).default)
        ]
    }
}
